/**
 * Active-filter accounting for the CRM list views.
 *
 * Every list page shows a "Filters" button with a count badge, and switches its
 * empty state between "no matching X" and "no X yet" depending on whether the
 * user has filtered. Both decisions need the same answer: which filters is the
 * user actually applying right now?
 *
 * The naive version — `Object.values(filters).some(v => v !== "")` — is wrong in
 * three ways, and every list page except /tickets shipped it:
 *
 *   1. A field whose default is NON-EMPTY is always "active". `tickets.state`
 *      defaults to "open", so the badge lit up on a clean page load, every time.
 *   2. View controls count as filters. `sort` and `assigned` change the view, not
 *      the result set, and shouldn't inflate a filter count.
 *   3. An UNSET field is not the same as a deviating one. Empty values are
 *      stripped from the URL and fall back to the declared default, so ""
 *      against a default of "open" means "open" — not a filter.
 *
 * So the comparison is against the DECLARED DEFAULT in config/searchConfig,
 * which is the same source of truth the filter drawer renders from. Passing the
 * field list in (rather than importing the config here) keeps this pure and
 * lets the tests state their own schema.
 */

import { SEARCH_CONFIG } from "config/searchConfig";

/**
 * "Unset" means the user has expressed nothing, so the declared default applies.
 *
 * Deliberately NOT falsy-based: `false` is a real answer for a boolean filter and
 * `0` is a real answer for a numeric one. Only absence and empty containers count.
 */
function isUnset(value) {
  if (value === undefined || value === null) return true;
  if (typeof value === "string") return value === "";
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

/**
 * Compare through String() because these values round-trip via the URL, where
 * everything is a string — a numeric filter read back from the querystring is
 * "50", not 50, and must still match a declared default of 50.
 *
 * Arrays compare order-insensitively: picking A then B is the same filter as
 * picking B then A.
 */
function sameValue(a, b) {
  if (Array.isArray(a) || Array.isArray(b)) {
    const left = (Array.isArray(a) ? a : []).map(String).sort();
    const right = (Array.isArray(b) ? b : []).map(String).sort();
    if (left.length !== right.length) return false;
    return left.every((v, i) => v === right[i]);
  }
  return String(a) === String(b);
}

/**
 * The keys the user is actively filtering on.
 *
 * @param {object} filters  current filter state (from useSearch)
 * @param {Array}  fields   `SEARCH_CONFIG[ns].fields` — needs `key` and `default`
 * @param {object} [opts]
 * @param {string[]} [opts.ignore]  keys that are view controls, not filters
 * @returns {string[]} deviating keys, in declaration order
 */
export function activeFilterKeys(filters = {}, fields = [], { ignore = [] } = {}) {
  const skip = new Set(ignore);
  const source = Array.isArray(fields) ? fields : [];

  return source
    .filter((field) => field && field.key && !skip.has(field.key))
    .filter((field) => {
      const value = filters[field.key];
      // Unset → the declared default applies → not a filter.
      if (isUnset(value)) return false;
      return !sameValue(value, field.default);
    })
    .map((field) => field.key);
}

/** How many filters to show on the Filters button badge. */
export function countActiveFilters(filters, fields, opts) {
  return activeFilterKeys(filters, fields, opts).length;
}

/**
 * Whether to show "no matching results" instead of the never-had empty state.
 */
export function hasActiveFilters(filters, fields, opts) {
  return activeFilterKeys(filters, fields, opts).length > 0;
}

/**
 * Field list for a namespace, so a page needs one import instead of two.
 * Returns [] for an unknown namespace rather than throwing — a miscounted
 * badge is not worth taking a page down over.
 */
export function filterFieldsFor(namespace) {
  return SEARCH_CONFIG[namespace]?.fields || [];
}
